// ---------------------------------------------------------------------------
// TemplateVarCallouts — awareness banners rendered inside a resource's Variables
// card (agent or grader). Surfaces two guardrails so a resource's `{{$var:…}}`
// references carry over accurately to eval templates:
//   • Undeclared `$var` (error): names referenced in the spec text but not
//     declared as variables — each with a one-click "Declare" that inserts the
//     row with the exact name (no retyping → no name typos).
//   • Bare `{{…}}` (warning): tokens that are neither `$var:` nor `$expr:` and
//     so are not substituted at run time — each convertible name offers a
//     one-click "Convert to $var".
// Renders nothing when there is nothing to surface, so it is safe to always
// mount (e.g. for grader types that normally use `$expr` bindings).
// ---------------------------------------------------------------------------

import { Body1, Button } from '@fluentui/react-components';

import type { BareTemplateToken } from '../../utils/templateRefs.js';
import { isConvertibleVarName } from '../../utils/templateRefs.js';

import { useStyles } from './TemplateVarCallouts.style.js';

export interface TemplateVarCalloutsProps<F extends string> {
  /** `$var` names referenced in the spec text but not declared as variables. */
  undeclaredVars: string[];
  /** Bare `{{…}}` tokens found across the spec text fields. */
  bareTokens: BareTemplateToken<F>[];
  /** Human labels for the field a bare token appears in (keyed by `field`). */
  fieldLabels: Record<F, string>;
  /** Insert a declared-variable row for `name` (with the exact name). */
  onDeclare: (name: string) => void;
  /** Rewrite the bare token in its field as `{{$var:INNER}}`. */
  onConvert: (token: BareTemplateToken<F>) => void;
  /** Hide the actions while the form is submitting. */
  disabled?: boolean;
}

export function TemplateVarCallouts<F extends string>({
  undeclaredVars,
  bareTokens,
  fieldLabels,
  onDeclare,
  onConvert,
  disabled = false,
}: TemplateVarCalloutsProps<F>) {
  const styles = useStyles();

  if (undeclaredVars.length === 0 && bareTokens.length === 0) return null;

  return (
    <>
      {undeclaredVars.length > 0 && (
        <div
          className={`${styles.callout} ${styles.calloutError}`}
          role="alert"
        >
          <div className={styles.calloutTitle}>
            Declare the variables used here
          </div>
          <Body1 className={styles.calloutText}>
            {'These are referenced with {{$var:…}} in the spec but not '}
            declared. Declare each so its value can be bound at evaluation time.
          </Body1>
          <div className={styles.calloutList}>
            {undeclaredVars.map((name) => (
              <div key={name} className={styles.calloutRow}>
                <code className={styles.calloutCode}>{name}</code>
                <Button
                  size="small"
                  disabled={disabled}
                  onClick={() => onDeclare(name)}
                >
                  Declare
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {bareTokens.length > 0 && (
        <div
          className={`${styles.callout} ${styles.calloutWarning}`}
          role="status"
        >
          <div className={styles.calloutTitle}>
            {'Unrecognized {{…}} tokens'}
          </div>
          <Body1 className={styles.calloutText}>
            {'Bare {{…}} is not a valid reference — the platform only '}
            {'substitutes {{$var:NAME}} or {{$expr:…}}. These will not '}
            be replaced at run time.
          </Body1>
          <div className={styles.calloutList}>
            {bareTokens.map((token) => (
              <div
                key={`${token.field}:${token.raw}`}
                className={styles.calloutRow}
              >
                <span>
                  <code className={styles.calloutCode}>{token.raw}</code>
                  {` in ${fieldLabels[token.field]}`}
                </span>
                {isConvertibleVarName(token.inner) && (
                  <Button
                    size="small"
                    disabled={disabled}
                    onClick={() => onConvert(token)}
                  >
                    Convert to $var
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
